import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Combobox } from '@/components/ui/combobox';
import { trpc } from '@/lib/trpc';
import { showSuccessToast, showErrorToast } from '@/hooks/use-mutation-with-toast';

const paymentTermsOptions = [
  { value: 'PREPAID', label: 'Prepaid' },
  { value: 'CREDIT_15', label: 'Net 15 Days' },
  { value: 'CREDIT_30', label: 'Net 30 Days' },
  { value: 'CREDIT_60', label: 'Net 60 Days' },
] as const;

type PaymentTerms = typeof paymentTermsOptions[number]['value'];

// Any non-dedicated cari type code (built-in generics ORT/P/AH/E/X/I plus
// admin-created custom types).
export type GenericCariType = string;

interface GenericCariFormData {
  companyName: string;
  contactName?: string;
  email?: string;
  phone?: string;
  website?: string;
  address?: string;
  city?: string;
  country?: string;
  taxId?: string;
  paymentTerms?: PaymentTerms;
  notes?: string;
  isActive: boolean;
}

export interface GenericCari {
  id: string;
  companyName: string;
  contactName?: string | null;
  email?: string | null;
  phone?: string | null;
  website?: string | null;
  address?: string | null;
  city?: string | null;
  country?: string | null;
  taxId?: string | null;
  paymentTerms?: PaymentTerms | null;
  notes?: string | null;
  isActive: boolean;
}

interface GenericCariModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  cariType: GenericCariType;
  cari?: GenericCari;
  /** Display label for the type; custom types pass their registry name. */
  typeLabel?: string;
}

const TYPE_LABEL_KEY: Record<string, string> = {
  ORT: 'cari.partner',
  P: 'cari.personnel',
  AH: 'cari.openAccount',
  E: 'cari.government',
  X: 'cari.legal',
  I: 'cari.insurance',
};

export function GenericCariModal({
  open,
  onOpenChange,
  cariType,
  cari,
  typeLabel: typeLabelProp,
}: GenericCariModalProps) {
  const { t } = useTranslation('trading');
  const { t: tCommon } = useTranslation('common');
  const isEditing = !!cari;

  const labelKey = TYPE_LABEL_KEY[cariType];
  const typeLabel = typeLabelProp ?? (labelKey ? t(labelKey) : cariType);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue,
  } = useForm<GenericCariFormData>({
    defaultValues: {
      companyName: '',
      contactName: '',
      email: '',
      phone: '',
      website: '',
      address: '',
      city: '',
      country: '',
      taxId: '',
      paymentTerms: 'CREDIT_30',
      notes: '',
      isActive: true,
    },
  });

  const utils = trpc.useUtils();

  const createMutation = trpc.tradingCari.createSupplierCari.useMutation({
    onSuccess: () => {
      utils.tradingCari.list.invalidate();
      utils.tradingCari.counts.invalidate();
      showSuccessToast(t('cari.cariCreated', { type: typeLabel }));
      onOpenChange(false);
      reset();
    },
    onError: (error) => {
      showErrorToast(error.message);
    },
  });

  const updateMutation = trpc.tradingCari.updateSupplierCari.useMutation({
    onSuccess: () => {
      utils.tradingCari.list.invalidate();
      utils.tradingCari.counts.invalidate();
      showSuccessToast(t('cari.cariUpdated', { type: typeLabel }));
      onOpenChange(false);
    },
    onError: (error) => {
      showErrorToast(error.message);
    },
  });

  useEffect(() => {
    if (open) {
      if (cari) {
        reset({
          companyName: cari.companyName,
          contactName: cari.contactName || '',
          email: cari.email || '',
          phone: cari.phone || '',
          website: cari.website || '',
          address: cari.address || '',
          city: cari.city || '',
          country: cari.country || '',
          taxId: cari.taxId || '',
          paymentTerms: cari.paymentTerms || 'CREDIT_30',
          notes: cari.notes || '',
          isActive: cari.isActive,
        });
      } else {
        reset({
          companyName: '',
          contactName: '',
          email: '',
          phone: '',
          website: '',
          address: '',
          city: '',
          country: '',
          taxId: '',
          paymentTerms: 'CREDIT_30',
          notes: '',
          isActive: true,
        });
      }
    }
  }, [open, cari, reset]);

  const onSubmit = (data: GenericCariFormData) => {
    const payload = {
      cariType,
      ...data,
      contactName: data.contactName || undefined,
      email: data.email || undefined,
      phone: data.phone || undefined,
      website: data.website || undefined,
      address: data.address || undefined,
      city: data.city || undefined,
      country: data.country || undefined,
      taxId: data.taxId || undefined,
      paymentTerms: data.paymentTerms || undefined,
      notes: data.notes || undefined,
    };

    if (isEditing && cari) {
      updateMutation.mutate({ id: cari.id, ...payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEditing
              ? t('cari.editCari', { type: typeLabel })
              : t('cari.addCari', { type: typeLabel })}
          </DialogTitle>
          <DialogDescription>
            {t('cari.cariDesc', { type: typeLabel })}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="companyName">{t('purchasing.suppliers.companyName')}</Label>
            <Input
              id="companyName"
              {...register('companyName', { required: tCommon('required') })}
            />
            {errors.companyName && (
              <p className="text-sm text-red-500">{errors.companyName.message}</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="contactName">{t('purchasing.suppliers.contactName')}</Label>
              <Input id="contactName" {...register('contactName')} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="taxId">{t('purchasing.suppliers.taxId')}</Label>
              <Input id="taxId" {...register('taxId')} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="email">{t('purchasing.suppliers.email')}</Label>
              <Input id="email" type="email" {...register('email')} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">{t('purchasing.suppliers.phone')}</Label>
              <Input id="phone" {...register('phone')} />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="website">{t('purchasing.suppliers.website')}</Label>
            <Input id="website" type="url" {...register('website')} />
          </div>

          <div className="space-y-2">
            <Label htmlFor="address">{t('purchasing.suppliers.address')}</Label>
            <Input id="address" {...register('address')} />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="city">{t('purchasing.suppliers.city')}</Label>
              <Input id="city" {...register('city')} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="country">{t('purchasing.suppliers.country')}</Label>
              <Input id="country" {...register('country')} />
            </div>
          </div>

          <div className="space-y-2">
            <Label>{t('purchasing.suppliers.paymentTerms')}</Label>
            <Combobox
              options={paymentTermsOptions.map(pt => ({ value: pt.value, label: pt.label }))}
              value={watch('paymentTerms') || 'CREDIT_30'}
              onValueChange={(value) => setValue('paymentTerms', value as PaymentTerms)}
              placeholder={t('purchasing.suppliers.selectPaymentTerms')}
              searchPlaceholder={tCommon('search')}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">{t('purchasing.suppliers.notes')}</Label>
            <Textarea id="notes" {...register('notes')} rows={3} />
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="isActive"
              {...register('isActive')}
              className="h-4 w-4 rounded border-gray-300"
            />
            <Label htmlFor="isActive">{t('purchasing.suppliers.isActive')}</Label>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
              {tCommon('cancel')}
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isEditing ? tCommon('save') : tCommon('create')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
